import type { Stage } from "./types";

/**
 * The eight stages of the Sight Reading course.
 *
 * The printed book has an implicit order; this makes it explicit and fills the
 * holes the audit found. Two things drive the ordering:
 *
 *  1. THE BASS-CLEF HOLE. In the printed book the bass clef first appears at
 *     piece 10 — and it appears already inside a two-hand grand staff, so the
 *     student meets a new clef and hands-together in the same breath. Stage 3
 *     exists purely to fix that: bass clef alone, one hand, before any grand
 *     staff.
 *  2. THE SPIRAL. Twinkle appears three times (melody, then left hand, then
 *     arpeggios) and Jesu Joy twice (melody, then harmony). The student re-reads
 *     music they already know while the texture gets harder underneath them.
 *     That is the best idea in the book, so it is named here rather than left to
 *     chance.
 *
 * `pieces` holds score slugs. A slug that has no score yet simply does not
 * appear on the stage map — so this file can describe the finished curriculum
 * while transcription is still catching up.
 */
export const STAGES: Stage[] = [
  {
    n: 1,
    title: "Five fingers, one hand",
    aim: "Read a single line inside one hand position, without moving your wrist.",
    pieces: ["first-steps", "rainy-day", "twinkle-melody", "two-by-two"],
  },
  {
    n: 2,
    title: "Leaving the position",
    aim: "Move beyond five notes: scale runs, thumb-under, and your first sharps and flats.",
    pieces: ["g-major-run", "feeding-ducks", "a-major-climb", "stop-and-go"],
  },
  {
    n: 3,
    title: "Reading the bass clef",
    aim: "Learn the second clef on its own, one hand at a time, before the two hands ever meet.",
    // New material — the rung the printed book is missing entirely.
    pieces: ["bass-first-look", "walking-down", "left-hand-lullaby"],
  },
  {
    n: 4,
    title: "Three beats in a bar",
    aim: "Feel a bar of three, and read a line that breathes in threes rather than fours.",
    pieces: ["jesu-joy-melody", "a-minor-waltz", "three-blind-mice"],
  },
  {
    n: 5,
    title: "Two hands begin",
    aim: "Put the clefs together. One hand holds while the other moves.",
    pieces: ["twinkle-left-hand", "mirror", "puff-the-magic-dragon", "ode-to-joy"],
  },
  {
    n: 6,
    title: "Texture and pattern",
    aim: "Read an accompaniment as a shape instead of note by note — arpeggios, stride, running quavers.",
    pieces: ["twinkle-arpeggios", "jesu-joy-harmony", "marching-forward", "boat-song",
             "irish-dancing"],
  },
  {
    n: 7,
    title: "Song reading",
    aim: "Read a melody you already know by ear, and let the ear check the eye.",
    pieces: ["bare-necessities", "over-the-rainbow-c", "over-the-rainbow-ab",
             "meet-the-flintstones"],
  },
  {
    n: 8,
    title: "Character pieces",
    aim: "Everything at once — ornaments, dynamics, blue notes, and a piece to perform.",
    pieces: ["fun-at-the-park", "storm-coming", "rock-n-roll"],
  },
];

export const STAGE_OF: Record<string, number> = Object.fromEntries(
  STAGES.flatMap((s) => s.pieces.map((p) => [p, s.n])),
);

/** Curriculum order across the whole course, ignoring pieces not yet built. */
export function orderedSlugs(available: Set<string>): string[] {
  return STAGES.flatMap((s) => s.pieces).filter((p) => available.has(p));
}
